<template>
    <!-- Nothing when the flag is off (persisted cards from an earlier flag-on period); a failed write/show says so. -->
    <section
        v-if="artifactsEnabled && (cards.length > 0 || isList || writeFailed || showFailed)"
        class="flex w-full max-w-xl flex-col gap-2"
        :aria-label="t('artifacts.card.sectionLabel')"
        data-testid="artifact-tool-result">
        <p v-if="writeFailed" class="details text-ink-muted" data-testid="artifact-tool-error">{{ t("artifacts.card.failed") }}</p>
        <p v-else-if="showFailed" class="details text-ink-muted" data-testid="artifact-tool-error">{{ t("artifacts.card.openFailed") }}</p>
        <p v-if="isList" class="details text-ink-muted" data-testid="artifact-list-count">
            {{ cards.length === 0 ? t("artifacts.card.emptyList") : t("artifacts.card.count", { count: cards.length }) }}
        </p>
        <BaseCard
            v-for="card in cards"
            :key="card.artifact_id"
            as="div"
            padding="sm"
            radius="lgr"
            class="flex w-full items-center gap-3 text-left"
            data-testid="artifact-card"
            :data-artifact-id="card.artifact_id">
            <span class="icon" aria-hidden="true"><i :class="artifactKindIconClass(card.kind)"></i></span>
            <span class="min-w-0 flex-1">
                <span class="nadia-chat-bold block truncate text-ink" data-testid="artifact-card-title">{{ card.title }}</span>
                <span class="flex flex-wrap items-center scaffold-details-small text-ink-muted">
                    <span>{{ kindLabel(card.kind) }}</span>
                    <span class="before:mx-2 before:content-['·']" data-testid="artifact-card-version">
                        {{ t("artifacts.card.version", { version: card.version_number }) }}
                    </span>
                    <span v-if="footer(card)" class="truncate before:mx-2 before:content-['·']" data-testid="artifact-card-footer">{{ footer(card) }}</span>
                </span>
            </span>
            <BaseButton
                theme="neutral"
                size="sm"
                class="shrink-0"
                :aria-label="t('artifacts.card.openNamed', { title: card.title })"
                data-testid="artifact-card-open"
                @click="open(card)">
                {{ t("artifacts.card.openShort") }}
            </BaseButton>
        </BaseCard>
    </section>
</template>

<script setup lang="ts">
import type { GlobalProperties } from "~vue/appTypes";
import { ACTION } from "~vue/chatActions";
import { artifactKindIconClass, artifactKindLabel } from "~vue/ChatDetail/artifacts/artifacts";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import { CHAT_EVENT } from "~vue/events";
import type { ArtifactSummary, ChatMessageLine, ParseActionsArtifactAction } from "~vue/types/chat";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, onMounted } from "vue";
import { useI18n } from "vue-i18n";

const {
    action = null,
    name = "",
    messageId = null,
    lineIdx = null,
} = defineProps<{ action?: ChatMessageLine | null; name?: string; messageId?: number | null; lineIdx?: number | null }>();

const { t } = useI18n();
const { emitter } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);
const artifactsEnabled = inject<boolean>("artifactsEnabled", false);
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);

const result = computed<ParseActionsArtifactAction["action_params"] | null>(() => {
    if (action?.type !== "action") {
        return null;
    }
    const artifactActions: string[] = [ACTION.CREATE_ARTIFACT, ACTION.READ_ARTIFACT, ACTION.EDIT_ARTIFACT, ACTION.REWRITE_ARTIFACT, ACTION.LIST_ARTIFACTS, ACTION.SHOW_ARTIFACT];
    return artifactActions.includes(action.action_name) ? (action as ParseActionsArtifactAction).action_params : null;
});

const WRITE_ACTIONS: string[] = [ACTION.CREATE_ARTIFACT, ACTION.EDIT_ARTIFACT, ACTION.REWRITE_ARTIFACT];
const writeFailed = computed(() => WRITE_ACTIONS.includes(name) && Boolean(result.value?.error));
const showFailed = computed(() => name === ACTION.SHOW_ARTIFACT && Boolean(result.value?.error));

const isList = computed(() => name === ACTION.LIST_ARTIFACTS && result.value !== null && !result.value.error && result.value.list_limit_reached !== true);

const cards = computed<ArtifactSummary[]>(() => {
    const params = result.value;
    // Reads are Nadia's own bookkeeping, like Claude/ChatGPT: nothing to show the user.
    if (!params || params.error || name === ACTION.READ_ARTIFACT) {
        return [];
    }
    if (Array.isArray(params.artifacts)) {
        return params.artifacts;
    }
    return params.artifact ? [params.artifact] : [];
});

const kindLabel = (kind: string): string => (kind === "document" ? t("artifacts.document") : artifactKindLabel(kind));

const footer = (card: ArtifactSummary): string => {
    const params = result.value;
    if (name === ACTION.EDIT_ARTIFACT && typeof params?.edits_applied === "number") {
        return t("artifacts.card.updated", { count: params.edits_applied });
    }
    if (name === ACTION.REWRITE_ARTIFACT) {
        return t("artifacts.card.rewritten");
    }
    if (name === ACTION.SHOW_ARTIFACT) {
        return t("artifacts.card.shown");
    }
    if (name === ACTION.CREATE_ARTIFACT) {
        return t("artifacts.card.created");
    }
    return card.change_summary ?? "";
};

const open = (card: ArtifactSummary) => {
    emitter?.emit(CHAT_EVENT.OPEN_ARTIFACT, { artifactId: card.artifact_id, versionNumber: card.version_number });
    logUserInteraction(
        "artifact_card_opened",
        { artifact_id: card.artifact_id, version_number: card.version_number, kind: card.kind, chat_message_id: messageId, line_idx: lineIdx, tool_name: name },
        coachingSessionUuid,
    );
};

onMounted(() => {
    // A revision's card mounts the moment the tool result arrives; tell an open pane for the
    // same artifact to jump to the new version. Historical cards mounting on reload are
    // ignored because no pane is open yet.
    const params = result.value;
    if ((name === ACTION.EDIT_ARTIFACT || name === ACTION.REWRITE_ARTIFACT) && params?.artifact && !params.error) {
        emitter?.emit(CHAT_EVENT.ARTIFACT_UPDATED, { artifactId: params.artifact.artifact_id, versionNumber: params.artifact.version_number });
    }
});
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    /* Neutral tile: the kind icon labels the document, it is not an action, so it stays off the brand accent. */
    .icon {
        @apply flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-sunken text-ink-muted;
    }
}
</style>
